import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Wrench, Mail, Lock, User, Building, Loader2, ArrowRight, Check, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    workshopName: ''
  });
  
  // Password recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState(1); // 1: enter email, 2: show code, 3: reset password
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [displayedCode, setDisplayedCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('¡Bienvenido de vuelta!');
      } else {
        await register(formData.email, formData.password, formData.name, formData.workshopName);
        toast.success('¡Cuenta creada exitosamente!');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Google Auth will be re-implemented in a future update

  const handleRequestCode = async () => {
    if (!recoveryEmail) {
      toast.error('Ingresa tu email');
      return;
    }
    
    setRecoveryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/recovery-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al generar código');
      }
      
      setDisplayedCode(data.recovery_code);
      setRecoveryStep(2);
      toast.success('Código generado. ¡Guárdalo!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!recoveryCode) {
      toast.error('Ingresa el código de recuperación');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setRecoveryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recoveryEmail,
          recovery_code: recoveryCode,
          new_password: newPassword
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = {};
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al restablecer contraseña');
      }
      
      toast.success('¡Contraseña actualizada! Inicia sesión');
      closeRecoveryModal();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRecoveryLoading(false);
    }
  };

  const closeRecoveryModal = () => {
    setShowRecovery(false);
    setRecoveryStep(1);
    setRecoveryEmail('');
    setRecoveryCode('');
    setDisplayedCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const features = [
    'Gestiona clientes y vehículos',
    'Historial de servicios completo',
    'Calcula utilidades automáticamente',
    'Funciona en móvil y escritorio'
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 md:p-16">
        <div className="max-w-md mx-auto w-full">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-[#1E3A5F] flex items-center justify-center">
              <Wrench className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900">TallerMec Pro</span>
          </div>

          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {isLogin ? 'Bienvenido de vuelta' : 'Crea tu cuenta'}
          </h1>
          <p className="text-slate-500 mb-8">
            {isLogin 
              ? 'Ingresa tus credenciales para continuar' 
              : 'Comienza a gestionar tu taller hoy'}
          </p>



          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name">Nombre completo</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Juan Pérez"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="pl-10"
                      required={!isLogin}
                      data-testid="name-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="workshop">Nombre del taller (opcional)</Label>
                  <div className="relative mt-1">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="workshop"
                      type="text"
                      placeholder="Taller Mecánico García"
                      value={formData.workshopName}
                      onChange={(e) => setFormData({ ...formData, workshopName: e.target.value })}
                      className="pl-10"
                      data-testid="workshop-input"
                    />
                  </div>
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">Correo electrónico</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="pl-10"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#1E3A5F] hover:bg-[#162e4d]"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>

          {isLogin && (
            <p className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setShowRecovery(true)}
                className="text-sm text-slate-500 hover:text-[#1E3A5F] hover:underline"
                data-testid="forgot-password-btn"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </p>
          )}

          <p className="mt-4 text-center text-sm text-slate-500">
            {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#1E3A5F] font-medium hover:underline"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? 'Regístrate' : 'Inicia sesión'}
            </button>
          </p>
        </div>
      </div>

      {/* Right side - Hero */}
      <div className="hidden lg:flex w-1/2 hero-section items-center justify-center p-16 relative">
        <div className="relative z-10 max-w-lg text-center">
          <h2 className="text-4xl font-bold mb-6">
            Digitaliza tu taller mecánico
          </h2>
          <p className="text-xl text-white/80 mb-8">
            La herramienta más completa para gestionar clientes, servicios e inventario de tu taller.
          </p>
          <ul className="space-y-4 text-left">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Password Recovery Modal */}
      <Dialog open={showRecovery} onOpenChange={(open) => !open && closeRecoveryModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Recuperar Contraseña
            </DialogTitle>
          </DialogHeader>
          
          {recoveryStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">
                Ingresa tu email y te generaremos un código de recuperación.
              </p>
              <div>
                <Label htmlFor="recovery-email">Email</Label>
                <Input
                  id="recovery-email"
                  type="email"
                  value={recoveryEmail}
                  onChange={(e) => setRecoveryEmail(e.target.value)}
                  placeholder="tu@email.com"
                  data-testid="recovery-email-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeRecoveryModal}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleRequestCode} 
                  disabled={recoveryLoading}
                  className="bg-[#1E3A5F]"
                >
                  {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generar Código'}
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {recoveryStep === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-center">
                <p className="text-sm text-amber-800 mb-2">Tu código de recuperación es:</p>
                <p className="text-3xl font-mono font-bold text-amber-900 tracking-widest">
                  {displayedCode}
                </p>
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Guarda este código. Expira en 15 minutos.
                </p>
              </div>
              <DialogFooter>
                <Button 
                  onClick={() => setRecoveryStep(3)} 
                  className="w-full bg-[#1E3A5F]"
                >
                  Continuar
                </Button>
              </DialogFooter>
            </div>
          )}
          
          {recoveryStep === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="recovery-code">Código de Recuperación</Label>
                <Input
                  id="recovery-code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  className="text-center text-xl tracking-widest"
                  data-testid="recovery-code-input"
                />
              </div>
              <div>
                <Label htmlFor="new-password">Nueva Contraseña</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirmar Contraseña</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRecoveryStep(2)}>
                  Atrás
                </Button>
                <Button 
                  onClick={handleResetPassword} 
                  disabled={recoveryLoading}
                  className="bg-[#1E3A5F]"
                >
                  {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restablecer'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
